/**
 * Workspace thumbnails for the library: a small picture of the canvas, taken as a workspace
 * is left.
 *
 * It is taken while the user waits for the next workspace, so nothing here may hold the page
 * up. In the desktop app the shell takes the picture, outside the page
 * (captureCanvasByShell). Anywhere else it is drawn here from what the page already holds
 * (drawCanvasThumbnail): the node cards in view and the pictures on them, as far as a time
 * budget goes. Nothing is fetched and no part of the page is copied.
 */

import { desktopApp } from './desktopFolder';
import { withinTime } from './pendingSaves';
import { logger } from './logger';

/** Thumbnails are square: the library shows them in square frames, cropped to fill. */
export const THUMBNAIL_SIZE = 360;
const THUMBNAIL_QUALITY = 0.7;
/** How long a leave waits for the shell's picture before going on without it. */
export const SHELL_CAPTURE_WAIT_MS = 300;
/** The picture is stored inside the workspace's own document: one the shell sends back larger than this is not used. */
const MAX_THUMBNAIL_CHARS = 200_000;

/**
 * Time the drawing may spend on pictures and video frames (the cards themselves cost next to
 * nothing). A picture has to be decoded again to be drawn: about 14 ns per pixel of the
 * original, measured in the desktop shell (36 to 59 ms for one 2560x1440 PNG). The largest
 * ones on the thumbnail are drawn first, for as long as their estimated cost fits; the rest
 * show as plain tiles.
 */
const MEDIA_BUDGET_MS = 40;
const DECODE_MS_PER_PIXEL = 14e-6;
const VIDEO_FRAME_MS = 8;
const CANVAS_COPY_MS = 2;
/** Pictures narrower or lower than this on the thumbnail are left out. */
const MIN_MEDIA_PX = 6;

const CANVAS_COLOR = '#FCFAF6';
const CARD_COLOR = '#FFFFFF';
const CARD_LINE = '#E8E2D6';
const TITLE_COLOR = '#F9F6F1';
const TILE_COLOR = '#F2EDE4';
/** The cards' corner radius and title bar height at 100% zoom (BaseNode: rounded-2xl, h-10). */
const CARD_RADIUS = 16;

export interface Box { x: number; y: number; width: number; height: number }

/** How far the app's panels reach over each edge of the canvas, in CSS pixels. */
export interface CoveredEdges { left?: number; top?: number; right?: number; bottom?: number }

/**
 * The part of the canvas a thumbnail shows: the largest square that the panels leave free, in
 * the middle of what they leave free. Whole pixels, as the shell wants them.
 */
export const pictureArea = (canvas: Box, covered: CoveredEdges = {}): Box => {
    const edge = (value?: number) => Math.max(0, value || 0);
    let free: Box = {
        x: canvas.x + edge(covered.left),
        y: canvas.y + edge(covered.top),
        width: canvas.width - edge(covered.left) - edge(covered.right),
        height: canvas.height - edge(covered.top) - edge(covered.bottom),
    };
    // Panels that leave nothing (a very small window): the whole canvas then
    if (free.width < 1 || free.height < 1) free = canvas;
    const side = Math.max(1, Math.floor(Math.min(free.width, free.height)));
    return {
        x: Math.round(free.x + (free.width - side) / 2),
        y: Math.round(free.y + (free.height - side) / 2),
        width: side,
        height: side,
    };
};

/**
 * Whether a picture from this address can be drawn and the result still be read: one from
 * another site taints the canvas, and reading it then throws.
 */
export const canDrawFrom = (url: string, pageOrigin: string): boolean => {
    if (!url) return false;
    if (url.startsWith('blob:') || url.startsWith('data:')) return true;
    try {
        return new URL(url, `${pageOrigin}/`).origin === pageOrigin;
    } catch {
        return false;
    }
};

/**
 * Where a picture goes inside its element's box, for the element's object-fit: the part of
 * the original that is taken and the box it fills (centred, as object-position is by default).
 */
export const fitMedia = (natural: { width: number; height: number }, box: Box, fit: string): { source: Box; target: Box } => {
    const whole: Box = { x: 0, y: 0, width: natural.width, height: natural.height };
    if (natural.width <= 0 || natural.height <= 0 || box.width <= 0 || box.height <= 0) return { source: whole, target: box };
    if (fit === 'cover') {
        const scale = Math.max(box.width / natural.width, box.height / natural.height);
        const width = box.width / scale;
        const height = box.height / scale;
        return { source: { x: (natural.width - width) / 2, y: (natural.height - height) / 2, width, height }, target: box };
    }
    if (fit === 'contain' || fit === 'scale-down') {
        const scale = Math.min(box.width / natural.width, box.height / natural.height);
        const width = natural.width * scale;
        const height = natural.height * scale;
        return { source: whole, target: { x: box.x + (box.width - width) / 2, y: box.y + (box.height - height) / 2, width, height } };
    }
    return { source: whole, target: box };
};

export interface MediaCandidate {
    /** What it shows: the same picture is only decoded once however often it is on the canvas. */
    key: string;
    kind: 'image' | 'video' | 'canvas';
    /** Pixels of the original. */
    pixels: number;
    /** Its area on the thumbnail. */
    area: number;
}

/**
 * Which pictures the drawing takes on, as indexes into `candidates` in the order to draw
 * them: the largest on the thumbnail first, each only if its estimated cost still fits.
 */
export const affordableMedia = (candidates: MediaCandidate[], budgetMs: number = MEDIA_BUDGET_MS): number[] => {
    const bySize = candidates.map((_, index) => index).sort((a, b) => candidates[b].area - candidates[a].area);
    const decoded = new Set<string>();
    const chosen: number[] = [];
    let spent = 0;
    for (const index of bySize) {
        const candidate = candidates[index];
        const cost = decoded.has(candidate.key) ? 0
            : candidate.kind === 'video' ? VIDEO_FRAME_MS
            : candidate.kind === 'canvas' ? CANVAS_COPY_MS
            : candidate.pixels * DECODE_MS_PER_PIXEL;
        if (spent + cost > budgetMs) continue;
        spent += cost;
        decoded.add(candidate.key);
        chosen.push(index);
    }
    return chosen;
};

const overlaps = (a: Box, b: Box): boolean => a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

const roundedRect = (ctx: CanvasRenderingContext2D, box: Box, radius: number): void => {
    const r = Math.max(0, Math.min(radius, box.width / 2, box.height / 2));
    ctx.beginPath();
    ctx.moveTo(box.x + r, box.y);
    ctx.lineTo(box.x + box.width - r, box.y);
    ctx.quadraticCurveTo(box.x + box.width, box.y, box.x + box.width, box.y + r);
    ctx.lineTo(box.x + box.width, box.y + box.height - r);
    ctx.quadraticCurveTo(box.x + box.width, box.y + box.height, box.x + box.width - r, box.y + box.height);
    ctx.lineTo(box.x + r, box.y + box.height);
    ctx.quadraticCurveTo(box.x, box.y + box.height, box.x, box.y + box.height - r);
    ctx.lineTo(box.x, box.y + r);
    ctx.quadraticCurveTo(box.x, box.y, box.x + r, box.y);
    ctx.closePath();
};

type MediaElement = HTMLImageElement | HTMLVideoElement | HTMLCanvasElement;

interface FoundMedia extends MediaCandidate {
    element: MediaElement;
    natural: { width: number; height: number };
    /** Its box on the thumbnail. */
    box: Box;
    fit: string;
    card: Box;
    radius: number;
    /** Loaded, and from where it can be drawn. */
    drawable: boolean;
}

const findCanvas = (container: HTMLElement | null): HTMLElement | null =>
    container || document.querySelector<HTMLElement>('[data-canvas-container="true"]');

/**
 * A picture of the canvas drawn in the page, from what it already holds: the background,
 * each node card in view as a rounded rectangle where it is on screen, and on it the
 * pictures and video frames the node shows, as far as the time budget goes.
 *
 * Undefined when there is no canvas to draw from or the result cannot be read.
 */
export const drawCanvasThumbnail = (container: HTMLElement | null, covered?: CoveredEdges): string | undefined => {
    try {
        const canvasElement = findCanvas(container);
        if (!canvasElement) return undefined;
        const bounds = canvasElement.getBoundingClientRect();
        if (bounds.width < 1 || bounds.height < 1) return undefined;
        const area = pictureArea({ x: bounds.left, y: bounds.top, width: bounds.width, height: bounds.height }, covered);
        const scale = THUMBNAIL_SIZE / area.width;
        const onPicture = (rect: DOMRect): Box => ({ x: (rect.left - area.x) * scale, y: (rect.top - area.y) * scale, width: rect.width * scale, height: rect.height * scale });
        const frame: Box = { x: 0, y: 0, width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE };

        const picture = document.createElement('canvas');
        picture.width = THUMBNAIL_SIZE;
        picture.height = THUMBNAIL_SIZE;
        // Kept in memory (not on the graphics card): what a picture really cost shows as soon
        // as it is drawn, so the budget can be kept
        const ctx = picture.getContext('2d', { willReadFrequently: true });
        if (!ctx) return undefined;

        ctx.fillStyle = getComputedStyle(canvasElement).backgroundColor || CANVAS_COLOR;
        ctx.fillRect(0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);

        // The cards in view, each with a plain tile where it shows a picture
        const media: FoundMedia[] = [];
        for (const node of Array.from(canvasElement.querySelectorAll<HTMLElement>('[data-node-id]'))) {
            const nodeRect = node.getBoundingClientRect();
            const card = onPicture(nodeRect);
            if (!overlaps(card, frame)) continue;
            // The zoom, from the card's size on screen against its own
            const zoom = node.offsetWidth > 0 ? nodeRect.width / node.offsetWidth : 1;
            const radius = CARD_RADIUS * zoom * scale;
            ctx.fillStyle = CARD_COLOR;
            roundedRect(ctx, card, radius);
            ctx.fill();
            ctx.save();
            ctx.clip();
            const title = node.querySelector<HTMLElement>('.drag-handle');
            if (title) {
                ctx.fillStyle = TITLE_COLOR;
                // The header as it is on screen: zoomed out, a card's inside is zoomed up (utils/cardZoom.ts)
                ctx.fillRect(card.x, card.y, card.width, title.getBoundingClientRect().height * scale);
            }
            for (const element of Array.from(node.querySelectorAll<MediaElement>('img, video, canvas'))) {
                const box = onPicture(element.getBoundingClientRect());
                if (box.width < MIN_MEDIA_PX || box.height < MIN_MEDIA_PX || !overlaps(box, frame)) continue;
                // Not what is hidden until the card is hovered
                if (typeof element.checkVisibility === 'function' && !element.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
                const isImage = element instanceof HTMLImageElement;
                const isVideo = element instanceof HTMLVideoElement;
                const natural = isImage ? { width: element.naturalWidth, height: element.naturalHeight }
                    : isVideo ? { width: element.videoWidth, height: element.videoHeight }
                    : { width: element.width, height: element.height };
                const source = isImage ? element.currentSrc || element.src : isVideo ? element.currentSrc || element.src : '';
                const loaded = isImage ? element.complete && natural.width > 0 : isVideo ? element.readyState >= 2 && natural.width > 0 : natural.width > 0 && natural.height > 0;
                ctx.fillStyle = TILE_COLOR;
                ctx.fillRect(box.x, box.y, box.width, box.height);
                media.push({
                    element,
                    key: isImage || isVideo ? source : `canvas-${media.length}`,
                    kind: isImage ? 'image' : isVideo ? 'video' : 'canvas',
                    pixels: natural.width * natural.height,
                    area: box.width * box.height,
                    natural,
                    box,
                    fit: getComputedStyle(element).objectFit,
                    card,
                    radius,
                    drawable: loaded && (!(isImage || isVideo) || canDrawFrom(source, window.location.origin)),
                });
            }
            ctx.restore();
            ctx.strokeStyle = CARD_LINE;
            ctx.lineWidth = 1;
            roundedRect(ctx, card, radius);
            ctx.stroke();
        }

        // The pictures on them, the largest first, while the budget lasts
        const drawable = media.filter(item => item.drawable);
        const started = performance.now();
        for (const index of affordableMedia(drawable)) {
            if (performance.now() - started > MEDIA_BUDGET_MS) break;
            const item = drawable[index];
            const { source, target } = fitMedia(item.natural, item.box, item.fit);
            ctx.save();
            roundedRect(ctx, item.card, item.radius);
            ctx.clip();
            ctx.beginPath();
            ctx.rect(item.box.x, item.box.y, item.box.width, item.box.height);
            ctx.clip();
            ctx.drawImage(item.element, source.x, source.y, source.width, source.height, target.x, target.y, target.width, target.height);
            ctx.restore();
            // Drawn now, not when the picture is read at the end: the time above then counts it
            ctx.getImageData(0, 0, 1, 1);
        }

        return picture.toDataURL('image/jpeg', THUMBNAIL_QUALITY);
    } catch (error) {
        logger.debug('Thumbnail', 'The canvas could not be drawn for a thumbnail:', error);
        return undefined;
    }
};

/** Whether this is the desktop app with a shell that can take the picture. */
export const shellCanCapture = (): boolean => typeof desktopApp()?.capturePage === 'function';

/**
 * A picture of the canvas taken by the desktop shell, outside the page: it costs the page
 * nothing, and shows the canvas exactly as it is. It is a picture of the window, so it has
 * to be asked for before anything covers the canvas.
 *
 * Undefined in a browser, with a shell that cannot do it, when it fails, and when it takes
 * longer than `waitMs`.
 */
export const captureCanvasByShell = async (
    container: HTMLElement | null,
    covered?: CoveredEdges,
    waitMs: number = SHELL_CAPTURE_WAIT_MS,
): Promise<string | undefined> => {
    try {
        const capture = desktopApp()?.capturePage;
        const canvasElement = capture ? findCanvas(container) : null;
        if (!capture || !canvasElement) return undefined;
        const bounds = canvasElement.getBoundingClientRect();
        if (bounds.width < 1 || bounds.height < 1) return undefined;
        const area = pictureArea({ x: bounds.left, y: bounds.top, width: bounds.width, height: bounds.height }, covered);
        const answer = await withinTime(capture(area, THUMBNAIL_SIZE), waitMs);
        if (answer?.ok && typeof answer.value === 'string' && answer.value.startsWith('data:image/') && answer.value.length <= MAX_THUMBNAIL_CHARS) {
            return answer.value;
        }
    } catch (error) {
        logger.debug('Thumbnail', 'The shell could not picture the canvas:', error);
    }
    return undefined;
};

const PLACEHOLDER_SIZE = 150;
const PLACEHOLDER_QUALITY = 0.85;

/**
 * Generate a simple placeholder thumbnail from workspace name
 */
export function generatePlaceholderThumbnail(workspaceName: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = PLACEHOLDER_SIZE;
  canvas.height = PLACEHOLDER_SIZE;
  const ctx = canvas.getContext('2d');

  if (!ctx) return '';

  // Gradient background
  const gradient = ctx.createLinearGradient(0, 0, PLACEHOLDER_SIZE, PLACEHOLDER_SIZE);
  gradient.addColorStop(0, '#e0e7ff'); // indigo-100
  gradient.addColorStop(1, '#f3e8ff'); // purple-100
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, PLACEHOLDER_SIZE, PLACEHOLDER_SIZE);

  // Text
  ctx.fillStyle = '#4f46e5'; // indigo-600
  ctx.font = 'bold 16px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Truncate name if too long
  const displayName = workspaceName.length > 25
    ? workspaceName.substring(0, 22) + '...'
    : workspaceName;
  ctx.fillText(displayName, PLACEHOLDER_SIZE / 2, PLACEHOLDER_SIZE / 2);

  return canvas.toDataURL('image/jpeg', PLACEHOLDER_QUALITY);
}
